import React, { Component } from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import { ChevronIcon } from '../icons'
import styles from './styles'
import { isIE, isAndroidChrome } from '../../utils/browser'

const LEFT = 'LEFT'
const RIGHT = 'RIGHT'

class ScrollableList extends Component {
  state = {
    currentScroll: 0,
    mouseFocus: false,
    showScrollButtons: false,
    innerWidth: 0,
    outerWidth: 0,
    hover: true,
  }

  wrapper = React.createRef()

  list = React.createRef()

  handleScroll = () => {
    this.setState({
      currentScroll: this.wrapper.current.scrollLeft,
    })
  }

  handleScrollClick = (e, direction) => {
    const el = this.wrapper.current
    const directionAsNumber = direction === LEFT ? -1 : 1
    const outerWidth = el?.offsetWidth

    if (!el || !outerWidth) return

    const section = this.list.current?.children[0]
    const card = section?.children[0]
    const cardWidth = card?.children[0]?.offsetWidth ?? card?.offsetWidth ?? 0

    if (!cardWidth) return

    const itemsPerPage = Math.floor(outerWidth / cardWidth)
    const scrollDistance = (itemsPerPage - 1) * cardWidth

    let currentDistance = 0

    const animateScrolling = () => {
      const scrollIncrement = Math.min(scrollDistance - currentDistance, cardWidth)
      el.scrollLeft += scrollIncrement * directionAsNumber
      currentDistance += scrollIncrement

      if (currentDistance < scrollDistance) {
        requestAnimationFrame(animateScrolling)
      }
    }

    requestAnimationFrame(animateScrolling)
  }

  handleMousedown = () => {
    this.setState({
      mouseFocus: true,
    })
  }

  handleBlur = () => {
    this.setState({
      mouseFocus: false,
    })
  }

  calculateContentSize = () => {
    // IE11 doesn't calculate the width of the scroll list properly.
    // Items don't wrap but somehow the container's scrolling width doesn't get calculated.
    // So we work it out the bad way instead.
    const el = this.list.current
    if (!el) return null
    const { children } = el
    if (!children) return null
    const listStyle = getComputedStyle(el)
    const listPadding = parseInt(listStyle.paddingLeft, 10) + parseInt(listStyle.paddingRight, 10)
    const fallbackWidth = Array.from(children).reduce((total, item) => {
      const itemWidth = parseFloat(item.offsetWidth, 10)
      const itemStyle = getComputedStyle(item)
      const itemMargin = parseInt(itemStyle.marginLeft, 10) + parseInt(itemStyle.marginRight, 10)
      return total + itemWidth + itemMargin
    }, 0) + listPadding
    return fallbackWidth
  }

  calculateWidths = () => {
    // eslint-disable-next-line max-len
    const innerWidth = (isIE() || this.props.isOverlay) ? this.calculateContentSize() : this.list.current?.offsetWidth
    const outerWidth = this.wrapper.current?.offsetWidth
    let hover = false
    if (window.matchMedia) { // jsdom doesn't support matchMedia
      // IE11 doesn't support hover media query, Android Chrome gives a false positive
      hover = (window.matchMedia('(hover: hover)').matches || isIE()) && !isAndroidChrome()
    }
    this.setState({
      showScrollButtons: innerWidth > outerWidth,
      innerWidth,
      outerWidth,
      hover,
    })
  }

  componentDidMount = () => {
    if (!this.props.children) return
    // iOS safari (10) will misreport the width on initial render ~ 1 in every 5 times
    // recalculatng after some time seems to fix it
    setTimeout(this.calculateWidths, 200)
    window.addEventListener('resize', debounce(this.calculateWidths, 200))
  }

  componentWillUnmount = () => {
    window.removeEventListener('resize', debounce(this.calculateWidths, 200))
  }

  render() {
    const {
      children, role, className, bannerEnabled, isOverlay, isTvGuide,
    } = this.props
    const {
      currentScroll,
      mouseFocus,
      showScrollButtons,
      innerWidth,
      outerWidth,
      hover,
    } = this.state
    return (
      <div className="ScrollableList">
        { showScrollButtons && hover && (
          <div className="scroller-buttons">
            <button
              className={`scroller-button scroll-left ${isOverlay ? 'background' : ''} ${isTvGuide ? 'tv-guide' : ''} ${mouseFocus ? 'nofocus' : ''}`}
              disabled={currentScroll === 0}
              onBlur={this.handleBlur}
              onClick={e => this.handleScrollClick(e, LEFT)}
              onMouseDown={this.handleMousedown}
              type="button"
            >
              <span>Scroll menu left</span>
              <ChevronIcon direction="left" color="#fff" width="18" height="16" />
            </button>
            <button
              className={`scroller-button scroll-right  ${isOverlay ? 'background' : ''} ${isTvGuide ? 'tv-guide' : ''} ${mouseFocus ? 'nofocus' : ''}`}
              disabled={currentScroll >= (innerWidth - outerWidth)}
              onBlur={this.handleBlur}
              onClick={e => this.handleScrollClick(e, RIGHT)}
              onMouseDown={this.handleMousedown}
              type="button"
            >
              <span>Scroll menu right</span>
              <ChevronIcon direction="right" color="#fff" width="18" height="16" />
            </button>
          </div>
        )}

        <div
          className={`list-outer llllll ${className} ${bannerEnabled ? 'banner' : ''} ${isOverlay && !isTvGuide ? 'overlay' : ''} ${isTvGuide ? 'tv-guide' : ''}`}
          style={this.props.subTab || isOverlay ? { border: 'none', marginTop: '5px' } : {}}
          ref={this.wrapper}
          onScroll={this.handleScroll}
        >
          <ul
            // listen for load events from children with images
            // so we can resize if needed
            onLoad={this.calculateWidths}
            role={role && role}
            style={this.props.subTab ? { border: 'none' } : {}}
            className={`${className} ${(isOverlay && showScrollButtons) ? 'position' : ''}`}
            ref={this.list}
          >
            { children }
          </ul>
        </div>
        <style jsx>{styles}</style>
      </div>
    )
  }
}

ScrollableList.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  role: PropTypes.string,
  className: PropTypes.string,
  subTab: PropTypes.bool,
  bannerEnabled: PropTypes.bool,
  isOverlay: PropTypes.bool,
  isTvGuide: PropTypes.bool,
}

ScrollableList.defaultProps = {
  children: null,
  role: null,
  className: null,
  subTab: false,
  bannerEnabled: false,
  isOverlay: false,
  isTvGuide: false,
}

export default ScrollableList
